import { Injectable } from '@angular/core';
import { set } from 'idb-keyval';
import { get } from 'idb-keyval';

@Injectable({
  providedIn: 'root'
})
export class EncryptionService {

  constructor() { }





  async initiateEncryption() {
    try {
      //create a new key pair
      const frontendKeys = await this.generateX25519Keys();
      console.log("Frontend Public Key", frontendKeys);
      // export the public key and store it in session storage
      const frontendPublicKeyBase64 = await this.exportPublicKey(frontendKeys.publicKey);
      sessionStorage.setItem("frontendPublicKey", frontendPublicKeyBase64);
      localStorage.setItem("frontendPublicKey", frontendPublicKeyBase64);
      //export the private key and store it in session storage
      // const frontendPrivateKeyBase64 = await this.exportPrivateKey(frontendKeys.privateKey);
      // console.log("Frontend Private Key", frontendPrivateKeyBase64);
      // sessionStorage.setItem("frontendPrivateKey", frontendPrivateKeyBase64);
      await this.setKey('frontendPrivateKey', frontendKeys.privateKey);
      //import pre shared backend public key
      const backendPublicKeyString = "MCowBQYDK2VuAyEAgN+Y9OCr1K40SZIUpBJ6/Y0V5yZYgMBT0Fh5ZgMbvxs=";
      const backendPublicKey = await this.importPublicKey(backendPublicKeyString);
      // Derive shared secret using frontend private key and backend public key
      const sharedSecret = await this.deriveSharedSecret(frontendKeys.privateKey, backendPublicKey);
      // Derive AES key from shared secret
      const frontendAESkey = await this.deriveAESKey(sharedSecret);
    } catch (e) {
      console.error("Error in initiateEncryption", e);
    }



  }

  // async setKey(keyName: string, value: any): Promise<void> {
  //   try {
  //     await set(keyName, value);
  //     console.log(`Stored "${keyName}" in IndexedDB.`);
  //   } catch (error) {
  //     console.error(`Failed to store "${keyName}" in IndexedDB:`, error);
  //     localStorage.setItem(keyName, value);
  //   }
  // }

  // async getKey<T = any>(keyName: string): Promise<T | null> {
  //   try {
  //     const value = await get(keyName);
  //     if (value === undefined) {
  //       console.warn(`No value found in IndexedDB for key "${keyName}"`);
  //       return null;
  //     }
  //     return value as T;
  //   } catch (error) {
  //     console.error(`Failed to get "${keyName}" from IndexedDB:`, error);

  //     return localStorage.getItem(keyName) as T || null;
  //   }
  // }

  // Add this helper in the same service
  private isCryptoKey(obj: any): obj is CryptoKey {
    // Best-effort detection — CryptoKey shape has `type` + `algorithm`
    return !!obj && typeof obj === 'object' && typeof obj.type === 'string' && !!obj.algorithm;
  }

  /**
   * Store a value in localStorage. Handles CryptoKey (exports to base64) and ordinary JS values.
   * Keeps the API async so your existing calls don't need to change.
   */
  async setKey(keyName: string, value: any): Promise<void> {
    try {
      // CryptoKey -> export + store as JSON descriptor
      if (this.isCryptoKey(value)) {
        // private vs public
        if (value.type === 'private') {
          const exported = await this.exportPrivateKey(value); // pkcs8 base64
          const payload = JSON.stringify({
            __type: 'crypto-key',
            keyType: 'private',
            format: 'pkcs8',
            value: exported
          });
          localStorage.setItem(keyName, payload);
          console.log(`Stored private CryptoKey "${keyName}" in localStorage (exported PKCS8).`);
          return;
        } else if (value.type === 'public') {
          const exported = await this.exportPublicKey(value); // spki base64
          const payload = JSON.stringify({
            __type: 'crypto-key',
            keyType: 'public',
            format: 'spki',
            value: exported
          });
          localStorage.setItem(keyName, payload);
          console.log(`Stored public CryptoKey "${keyName}" in localStorage (exported SPKI).`);
          return;
        }
      }

      // Non-CryptoKey -> JSON stringify (safe for objects, arrays, primitives)
      localStorage.setItem(keyName, JSON.stringify({ __type: 'json', value }));
      console.log(`Stored "${keyName}" in localStorage.`);
    } catch (error) {
      console.error(`Failed to store "${keyName}" in localStorage:`, error);
    }
  }

  /**
   * Retrieve the key. If a CryptoKey was stored, it will import and return a CryptoKey.
   * Returns null if missing.
   */
  async getKey<T = any>(keyName: string): Promise<T | CryptoKey | null> {
    try {
      const raw = localStorage.getItem(keyName);
      if (raw === null) {
        console.warn(`No value found in localStorage for key "${keyName}"`);
        return null;
      }

      // try parse
      let parsed: any;
      try {
        parsed = JSON.parse(raw);
      } catch {
        // not JSON — legacy or corrupted; return raw string
        return raw as unknown as T;
      }

      if (parsed && parsed.__type === 'crypto-key') {
        // crypto key descriptor
        if (parsed.keyType === 'private') {
          const imported = await this.importPrivateKey(parsed.value);
          return imported as CryptoKey;
        } else if (parsed.keyType === 'public') {
          const imported = await this.importPublicKey(parsed.value);
          return imported as CryptoKey;
        } else {
          console.warn("Unknown crypto keyType in storage:", parsed.keyType);
          return null;
        }
      }

      if (parsed && parsed.__type === 'json') {
        return parsed.value as T;
      }

      // fallback: return parsed directly
      return parsed as T;
    } catch (error) {
      console.error(`Failed to get "${keyName}" from localStorage:`, error);
      return null;
    }
  }

  // Optional: convenience removers
  async removeKey(keyName: string) {
    localStorage.removeItem(keyName);
  }
  async clearAllKeys() {
    localStorage.clear();
  }


  async generateX25519Keys(): Promise<CryptoKeyPair> {
    const keyPair = await crypto.subtle.generateKey(
      {
        name: "X25519"
      },
      true, // Extractable keys (to share public key)
      ["deriveKey", "deriveBits"]
    );

    return keyPair as CryptoKeyPair;;
  }

  async exportPublicKey(key) {
    const exported = await crypto.subtle.exportKey("spki", key);
    return btoa(String.fromCharCode(...new Uint8Array(exported))); // Convert to Base64
  }

  async exportPrivateKey(privateKey: CryptoKey): Promise<string> {
    const exported = await crypto.subtle.exportKey('pkcs8', privateKey);
    return btoa(String.fromCharCode(...new Uint8Array(exported)));
  }


  async deriveSharedSecret(privateKey, publicKey) {
    return await crypto.subtle.deriveBits(
      {
        name: "X25519",
        public: publicKey
      },
      privateKey,
      256 // X25519 shared secret is always 256 bits
    );
  }

  async deriveAESKey(sharedSecret) {
    return await crypto.subtle.importKey(
      "raw",
      await crypto.subtle.digest("SHA-256", sharedSecret),
      { name: "AES-GCM", length: 256 },
      true,
      ["encrypt", "decrypt"]
    );
  }

  isValidBase64(str: string): boolean {
    try {
      return btoa(atob(str)) === str.replace(/\s+/g, '');
    } catch {
      return false;
    }
  }

  async encryptAES(key, plaintext) {
    let base64text = JSON.stringify(plaintext)
    const iv = crypto.getRandomValues(new Uint8Array(12)); // 12-byte IV for AES-GCM
    const encoder = new TextEncoder();
    const encryptedData = await crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv: iv,
        tagLength: 128 // 16-byte auth tag
      },
      key,
      encoder.encode(base64text)
    );

    // Concatenate IV and ciphertext
    const encryptedBytes = new Uint8Array(encryptedData);
    const finalData = new Uint8Array(iv?.length + encryptedBytes?.length);

    finalData.set(iv, 0); // Set IV at the beginning
    finalData.set(encryptedBytes, iv?.length); // Append ciphertext after IV

    return btoa(String.fromCharCode.apply(null, finalData)); // Convert to Base64
  }

  async decryptAES(key, encryptedBase64) {
    // console.log("Decrypting AES with key:", encryptedBase64);

    if (!/^[A-Za-z0-9+/=]+$/.test(encryptedBase64) || !encryptedBase64 || typeof encryptedBase64 !== 'string' || !this.isValidBase64(encryptedBase64)) {
      throw new Error("❌ decryptAES: Input is not valid base64");
    }
    // const encryptedData = Uint8Array.from(atob(encryptedBase64), c => c.charCodeAt(0));


    const cleanBase64 = encryptedBase64?.replace(/\s+/g, '');
    const encryptedData = Uint8Array.from(atob(cleanBase64), c => c.charCodeAt(0));



    const iv = encryptedData.slice(0, 12); // First 12 bytes = IV
    const ciphertext = encryptedData.slice(12); // Rest is ciphertext + authTag

    const decryptedData = await crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: iv,
        tagLength: 128
      },
      key,
      ciphertext
    );

    return new TextDecoder().decode(decryptedData);
  }







  async importPublicKey(base64Key) {
    try {
      // Convert Base64 to raw bytes
      const rawKey = Uint8Array.from(atob(base64Key), c => c.charCodeAt(0));

      // Import the public key
      return await crypto.subtle.importKey(
        "spki",          // Raw public key format
        rawKey,         // Key data
        { name: "X25519" },
        true,           // Extractable (allows exporting again if needed)
        []              // No key usages (only for key derivation)
      );
    } catch (error) {
      console.error("Error importing public key:", error);
      return null;
    }
  }

  async importPrivateKey(base64Key) {
    try {
      const rawKey = Uint8Array.from(atob(base64Key), c => c.charCodeAt(0));

      return await crypto.subtle.importKey(
        "pkcs8",
        rawKey,
        { name: "X25519" },
        true,
        ["deriveKey", "deriveBits"]
      );
    } catch (error) {
      console.error("Error importing private key:", error);
      return null;
    }
  }
}
